import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { ListChecks, ShieldCheck, KeyRound, LifeBuoy, Mail, ChevronRight, LifeBuoy as SupportIcon, Coins } from "lucide-react";

type Topic = {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: string[];
};

/**
 * Topics disponibles pour ecommerce v1 — chacun pointe vers une page existante
 * du dashboard qui documente le sujet. Pas de refunds (feature stripée).
 */
const TOPICS: Topic[] = [
  {
    to: "/transactions/statuses",
    icon: <ListChecks size={22} />,
    title: "Comprendre les statuts de paiement",
    description:
      "Cycle de vie d'une transaction PayDRC : pending → submitted → successful / failed. Ce qui est déclenché à chaque étape, les callbacks et les statuts terminaux.",
    tags: ["Transactions", "Statuts", "Callbacks"],
  },
  {
    to: "/merchant/roles",
    icon: <ShieldCheck size={22} />,
    title: "Rôles et permissions",
    description:
      "Vue d'ensemble des rôles marchand (merchant_admin, finance, it, commercial) et de ce que chacun peut voir ou modifier dans le dashboard.",
    tags: ["Équipe", "Sécurité", "Permissions"],
  },
  {
    to: "/merchant/api-keys",
    icon: <KeyRound size={22} />,
    title: "Clés API et intégration",
    description:
      "Où trouver votre clé API, comment la faire tourner en toute sécurité, et bonnes pratiques pour l'appel des endpoints PayDRC depuis vos systèmes.",
    tags: ["Intégration", "IT", "Sécurité"],
  },
  {
    to: "/finance/statements",
    icon: <Coins size={22} />,
    title: "Comprendre commissions et solde",
    description:
      "Différence entre solde actuel, dépôts réussis, commissions Moko (Freshpay + Telco), et net crédité sur votre wallet marchand. Pourquoi le solde peut différer du net cumulé.",
    tags: ["Finance", "Commissions", "Wallet"],
  },
  {
    to: "/support",
    icon: <SupportIcon size={22} />,
    title: "Ouvrir un ticket support",
    description:
      "Ouvrir un ticket vers l'équipe PayDRC pour toute question opérationnelle ou incident. Historique et pièces jointes conservés dans votre espace Support.",
    tags: ["Support", "Contact"],
  },
];

export function HelpPage() {
  return (
    <div>
      <PageHeader
        title="Centre d'aide"
        breadcrumbs={[{ label: "Aide" }]}
      />

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
              <LifeBuoy size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-[#181c32] text-base mb-1">
                Guides d'utilisation du dashboard ecommerce
              </div>
              <p className="text-sm text-[#3f4254] leading-relaxed">
                Retrouvez ici des raccourcis vers les sections clés du dashboard :
                comprendre les statuts, la tarification, les rôles de votre équipe
                et l'intégration technique côté API. Pour toute question hors de
                ces sujets, ouvrez un ticket support.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {TOPICS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="card hover:shadow-md transition-shadow group"
          >
            <div className="card-body">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  {t.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-[#181c32] text-base group-hover:text-brand-700 transition-colors">
                      {t.title}
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-[#b5b5c3] group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all"
                    />
                  </div>
                  <p className="text-sm text-[#3f4254] leading-relaxed">
                    {t.description}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 pl-16">
                {t.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-[#f5f8fa] text-[#7e8299]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="card">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#f5f8fa] text-[#3f4254] flex items-center justify-center shrink-0">
              <Mail size={20} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-[#181c32] text-sm mb-1">
                Besoin d'aide supplémentaire ?
              </div>
              <p className="text-sm text-[#3f4254] leading-relaxed">
                Si vous ne trouvez pas la réponse à votre question, écrivez à
                l'équipe PayDRC à{" "}
                <a
                  href="mailto:support@gofreshbakery.com"
                  className="text-brand-700 hover:underline font-medium"
                >
                  support@gofreshbakery.com
                </a>{" "}
                en précisant votre identifiant marchand (votre entité /
                <span className="font-mono"> m6VykAFzVHjQi</span>) et, si possible, la
                référence PayDRC ou marchand du paiement concerné.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
